/**
 * Physical constants for the player and the camera, in metres and seconds.
 *
 * These are placeholders tuned by feel on a device, not derived from anything. When they
 * change, they change here — no component should hard-code a height or a speed.
 */

/** Camera height above the player's feet. A little under average eye height. */
export const EYE_HEIGHT = 1.7;

/** Capsule radius. Wide enough not to slip through door frames, narrow enough to pass them. */
export const PLAYER_RADIUS = 0.3;

/**
 * Half-height of the capsule's cylindrical section, excluding the hemispherical caps.
 * Total standing height is 2 * (PLAYER_CAPSULE_HALF_HEIGHT + PLAYER_RADIUS) = 1.8m.
 */
export const PLAYER_CAPSULE_HALF_HEIGHT = 0.6;

/** Distance from the capsule's centre to the player's feet. */
export const PLAYER_CENTER_OFFSET = PLAYER_CAPSULE_HALF_HEIGHT + PLAYER_RADIUS;

/** Where the camera sits relative to the capsule's centre. */
export const CAMERA_OFFSET_FROM_CENTER = EYE_HEIGHT - PLAYER_CENTER_OFFSET;

export const WALK_SPEED = 3.2;

/**
 * Speed while sprinting — 1.75× the walk. Fast enough that a long corridor is worth
 * running down, slow enough that a three-metre corridor can still be steered and the
 * carried-box casts still resolve the step (design D4). The gesture that asks for it
 * belongs to the input producers; this is the only place the speed exists.
 */
export const SPRINT_SPEED = 5.6;

/**
 * Speed while dragging a table — half a walk. Slow enough that the weight is felt, fast
 * enough that moving a table down a corridor is not a chore, and deliberately below the
 * walk rather than above it: sprinting has no effect while dragging (design D6), so this
 * is the only pace a drag has.
 */
export const DRAG_SPEED = 1.6;

export const GRAVITY = -9.81;

/** Take-off speed. v²/2g ≈ 0.9 m — over a box or a step, never over a wall. */
export const JUMP_SPEED = 4.2;

/**
 * Pitch limit, just short of straight up and straight down. Reaching exactly vertical
 * makes yaw ambiguous and the view rolls — the classic gimbal artefact.
 */
export const MAX_PITCH = Math.PI / 2 - 0.01;

/** Skin width kept between the capsule and geometry, so contacts do not jitter. */
export const CHARACTER_OFFSET = 0.02;

/** Steeper than this and the player slides rather than climbs. */
export const MAX_SLOPE_CLIMB_ANGLE = (45 * Math.PI) / 180;
export const MIN_SLOPE_SLIDE_ANGLE = (30 * Math.PI) / 180;

/** How far below the feet the controller looks for ground when stepping down. */
export const SNAP_TO_GROUND_DISTANCE = 0.5;

export const CAMERA_FOV = 75;
export const CAMERA_NEAR = 0.1;
export const CAMERA_FAR = 100;

/**
 * Fixed physics step. Substeps are capped so that a frame hitch — a garbage collection
 * pause, a backgrounded tab — cannot cascade into the physics world trying to catch up
 * across hundreds of steps at once.
 */
export const PHYSICS_TIMESTEP = 1 / 60;
export const MAX_PHYSICS_SUBSTEPS = 4;
